<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import { onImgError } from '@shared/composables/useImgFallback'
import { MEDIA_TYPE } from '../../constants/propertyLifecycle'
import { downloadDocument, viewDocument } from '../../utils/propertyDocumentUtils'
import type { PropertyMedia } from '../../types/propertyTypes'

// The property's photos and videos: one large stage, a thumbnail strip under it,
// and View / Download for whichever item is on the stage.
//
// Public items only — the server sends exactly what the marketplace shows, so
// there is no review state to flag here.
const props = defineProps<{ media: PropertyMedia[] }>()

const activeIndex = ref(0)

// Media arrives cover-first from the server. Reset when the property changes so
// a stale index never points past a shorter list.
watch(
  () => props.media.map((item) => item.id).join('|'),
  () => {
    activeIndex.value = 0
  },
)

const activeMedia = computed<PropertyMedia | null>(() => props.media[activeIndex.value] ?? null)

const isVideo = (item: PropertyMedia) => item.mediaType === MEDIA_TYPE.video

const counter = computed(() => `${activeIndex.value + 1} of ${props.media.length}`)

function select(index: number) {
  activeIndex.value = index
}

const isSaving = ref(false)

async function handleDownload(item: PropertyMedia) {
  isSaving.value = true
  try {
    await downloadDocument(item.fileUrl, item.fileName)
  } finally {
    isSaving.value = false
  }
}
</script>

<template>
  <Box direction="col" gap="3" class="gallery">
    <Box v-if="activeMedia" class="gallery__stage">
      <!-- A raw <video> for the video case: the kit wraps images only, and a
           property walkthrough needs native controls. -->
      <video
        v-if="isVideo(activeMedia)"
        :src="activeMedia.fileUrl"
        class="gallery__media"
        controls
        preload="metadata"
      />
      <img
        v-else
        :src="activeMedia.fileUrl"
        :alt="activeMedia.categoryName ?? activeMedia.fileName"
        class="gallery__media"
        @error="onImgError"
      />

      <Box flex align="center" gap="2" class="gallery__caption">
        <Text v-if="activeMedia.categoryName" type="caption" weight="medium" is="span">
          {{ activeMedia.categoryName }}
        </Text>
        <Text type="caption" color="muted" is="span">{{ counter }}</Text>
      </Box>
    </Box>

    <Box v-if="activeMedia" flex align="center" justify="end" gap="2" class="gallery__actions">
      <Button
        label="View"
        icon="eye"
        variant="outline"
        color="secondary"
        size="small"
        @click="viewDocument(activeMedia.fileUrl)"
      />
      <Button
        label="Download"
        icon="download"
        variant="outline"
        color="secondary"
        size="small"
        :loading="isSaving"
        @click="handleDownload(activeMedia)"
      />
    </Box>

    <Box v-if="media.length > 1" flex gap="2" class="gallery__strip">
      <Box
        v-for="(item, index) in media"
        :key="item.id"
        is="button"
        type="button"
        :class="['gallery__thumb', index === activeIndex && 'gallery__thumb--active']"
        :aria-label="`View ${item.categoryName ?? item.fileName}`"
        :aria-current="index === activeIndex"
        @click="select(index)"
      >
        <img
          v-if="!isVideo(item)"
          :src="item.fileUrl"
          alt=""
          class="gallery__thumb-img"
          loading="lazy"
          @error="onImgError"
        />
        <Box v-else flex align="center" justify="center" class="gallery__thumb-video">
          <PiIcon name="play" class="gallery__thumb-icon" />
        </Box>
      </Box>
    </Box>
  </Box>
</template>

<style scoped>
.gallery__stage {
  position: relative;
  aspect-ratio: 16 / 9;
  border: 1px solid var(--p-content-border-color);
  border-radius: var(--p-border-radius-lg);
  overflow: hidden;
  background: var(--p-surface-100);
}

.gallery__media {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.gallery__caption {
  position: absolute;
  bottom: 0.75rem;
  left: 0.75rem;
  padding: 0.25rem 0.625rem;
  border-radius: 999px;
  background: var(--p-surface-0);
}

/* Scrolls horizontally rather than wrapping: a wrapping strip pushes the rest of
   the page down by an unpredictable amount as photos are added. */
.gallery__strip {
  overflow-x: auto;
  padding-bottom: 0.25rem;
  scrollbar-width: thin;
}

.gallery__thumb {
  position: relative;
  flex: 0 0 auto;
  width: 4.5rem;
  height: 3.5rem;
  padding: 0;
  border: 2px solid transparent;
  border-radius: var(--p-border-radius-md);
  overflow: hidden;
  background: var(--p-surface-100);
  cursor: pointer;
}

.gallery__thumb--active {
  border-color: var(--p-primary-500);
}

.gallery__thumb:focus-visible {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 2px;
}

.gallery__thumb-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.gallery__thumb-video {
  width: 100%;
  height: 100%;
  background: var(--p-surface-800, var(--p-surface-700));
  color: var(--p-surface-0);
}

.gallery__thumb-icon {
  font-size: 0.75rem;
}
</style>
